/**
 * "Open in app" helpers for the mobile-web smart-banner affordance.
 *
 * When someone opens the Tailspend *web* app in a phone browser — most often
 * from a shared group-invite or expense link — we offer to reopen the same
 * destination in the native app. The native app registers the `tailspend://`
 * URL scheme (see `apps/mobile/app.json`) and uses Expo Router, whose paths
 * mirror the web routes 1:1 (`/groups/:id/join`, `/groups/:id/...`), so the
 * current web path maps straight onto a deep link with no route table to keep
 * in sync.
 *
 * Everything here is pure and DOM-light so it can be unit-tested; the React
 * wiring lives in `components/OpenInAppBanner.tsx`.
 */

/** Native URL scheme registered by the mobile app (apps/mobile/app.json). */
export const APP_SCHEME = "tailspend";

/**
 * Store identifiers, mirrored from `apps/mobile/app.json` and
 * `pages/BrandPage.tsx`. Used to build store fallbacks and the iOS Smart App
 * Banner meta so a tap still lands somewhere useful when the app isn't
 * installed.
 */
export const IOS_APP_ID = "6777495715";
export const ANDROID_PACKAGE = "studio.kinesis.tailspend";

/** localStorage key remembering that the user dismissed the banner. */
export const DISMISS_STORAGE_KEY = "tailspend:open-in-app-dismissed";

/**
 * Build the `tailspend://` deep link for a web location. Expo Router maps app
 * paths to the same shape as the web routes, so we forward the path (minus its
 * leading slash) plus the query string. `tailspend://` opens the app's home.
 */
export function buildDeepLink(pathname: string, search = ""): string {
  const path = pathname.replace(/^\/+/, "");
  return `${APP_SCHEME}://${path}${search}`;
}

/** iOS App Store product URL. */
export function iosStoreUrl(): string {
  return `https://apps.apple.com/app/id${IOS_APP_ID}`;
}

/** Google Play product URL. */
export function androidStoreUrl(): string {
  return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
}

/**
 * Whether the current environment is a phone-web context where suggesting the
 * app makes sense: a touch-primary pointer *and* a narrow viewport, served
 * over http(s). The desktop app runs from `file://` and desktop browsers have
 * a fine pointer, so both are naturally excluded.
 *
 * Guarded for SSR / non-DOM (`window`/`matchMedia` absent) — returns false.
 */
export function isPhoneWeb(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  // The desktop shell loads from file://; never nag there.
  if (window.location?.protocol === "file:") return false;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.matchMedia("(max-width: 640px)").matches;
  return coarse && narrow;
}

/** Whether the user previously dismissed the banner. */
export function isDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === "1";
  } catch {
    // Private-mode / storage-disabled: treat as not dismissed but never throw.
    return false;
  }
}

/** Persist that the user dismissed the banner. */
export function rememberDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, "1");
  } catch {
    // Storage unavailable — the banner just reappears next visit. No-op.
  }
}
